/**
 * useAlertFiltering Hook
 *
 * Applies filters to alerts and alert history based on search query,
 * severity, days until stockout, and inventory level filters.
 */

import { useMemo } from 'react';
import type { EnhancedAlert, AlertSeverity } from '../../types/dashboard';

/**
 * Input parameters for useAlertFiltering
 */
export interface UseAlertFilteringInput {
  /** Current alerts (including optimistic updates) */
  alerts: EnhancedAlert[];
  /** Historical alerts (resolved/dismissed) */
  alertHistory: EnhancedAlert[];
  /** Debounced search query */
  searchQuery: string;
  /** Selected severity filters */
  severityFilter: AlertSeverity[];
  /** Minimum days until stockout filter */
  daysMin: string;
  /** Maximum days until stockout filter */
  daysMax: string;
  /** Minimum inventory level filter */
  inventoryMin: string;
  /** Maximum inventory level filter */
  inventoryMax: string;
  /** Current selected tab index (0 = Active Alerts) */
  selectedTab: number;
}

/**
 * Return type for useAlertFiltering hook
 */
export interface UseAlertFilteringReturn {
  /** Filtered active alerts based on all criteria */
  filteredAlerts: EnhancedAlert[];
  /** Filtered alert history based on search query */
  filteredHistory: EnhancedAlert[];
}

/**
 * Hook for filtering alerts and alert history
 *
 * Applies multiple filter criteria to alerts:
 * - Tab selection (Active Alerts = critical/warning only)
 * - Search query (product title, variant title, SKU, variant ID)
 * - Severity filter
 * - Days until stockout range
 * - Inventory level range
 *
 * Alert history only filters by search query.
 *
 * @param input - Alerts, history, and filter criteria
 * @returns Memoized filtered alerts and history
 */
export function useAlertFiltering({
  alerts,
  alertHistory,
  searchQuery,
  severityFilter,
  daysMin,
  daysMax,
  inventoryMin,
  inventoryMax,
  selectedTab,
}: UseAlertFilteringInput): UseAlertFilteringReturn {
  // Filter alerts based on selected tab and filters
  const filteredAlerts = useMemo(() => {
    const query = searchQuery.toLowerCase();

    return alerts.filter((alert) => {
      // Filter by tab selection (Active Alerts = critical/warning only)
      if (selectedTab !== 0) return false;
      const matchesTab = alert.severity === 'critical' || alert.severity === 'warning';

      // Filter by search query
      const matchesSearch =
        !query ||
        alert.variant_id.toString().toLowerCase().includes(query) ||
        (alert.product_title && alert.product_title.toLowerCase().includes(query)) ||
        (alert.variant_title && alert.variant_title.toLowerCase().includes(query)) ||
        (alert.variant_sku && alert.variant_sku.toLowerCase().includes(query));

      // Filter by severity
      const matchesSeverity =
        severityFilter.length === 0 || severityFilter.includes(alert.severity as AlertSeverity);

      // Filter by days until stockout
      let matchesDays = true;
      if (daysMin || daysMax) {
        const days = alert.days_until_stockout;
        const min = daysMin ? parseInt(daysMin, 10) : null;
        const max = daysMax ? parseInt(daysMax, 10) : null;
        if (min !== null && max !== null) matchesDays = days >= min && days <= max;
        else if (min !== null) matchesDays = days >= min;
        else if (max !== null) matchesDays = days <= max;
      }

      // Filter by inventory level
      let matchesInventory = true;
      if (inventoryMin || inventoryMax) {
        const inventory = alert.current_inventory;
        const min = inventoryMin ? parseInt(inventoryMin, 10) : null;
        const max = inventoryMax ? parseInt(inventoryMax, 10) : null;
        if (min !== null && max !== null) matchesInventory = inventory >= min && inventory <= max;
        else if (min !== null) matchesInventory = inventory >= min;
        else if (max !== null) matchesInventory = inventory <= max;
      }

      return matchesTab && matchesSearch && matchesSeverity && matchesDays && matchesInventory;
    });
  }, [alerts, selectedTab, searchQuery, severityFilter, daysMin, daysMax, inventoryMin, inventoryMax]);

  // Filter alert history
  const filteredHistory = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return alertHistory.filter((alert) => {
      const matchesSearch =
        !query ||
        (alert.product_title && alert.product_title.toLowerCase().includes(query)) ||
        alert.variant_id.toString().toLowerCase().includes(query);
      return matchesSearch;
    });
  }, [alertHistory, searchQuery]);

  return { filteredAlerts, filteredHistory };
}
